import api from './axiosInstance';

// Servicio de Negociación Administrativa (Persona Natural). Funciones async
// planas: única capa que llama `api`. Backend: AdminNegotiationProcessController
// (api/admin-negotiation/processes) + AdminNegotiationDocumentController
// (api/admin-negotiation/documents).

// ── Procesos manuales (flujo manual de viabilidad) ──────────────────────────
// Backend: AdminNegotiationProcessController (api/admin-negotiation/processes).
// Reemplaza el análisis automático: el proceso nace en la pestaña Documentos (botón
// "Nuevo proceso") o desde una tarea GC (viability_pending) que se decide a mano.

/** Crea un proceso manual sobre un caso del cliente: crea la tarea espejo VIAB/RENEG
 *  + check confirmed + acreedores + pipeline post-aprobación. caseId null/omitido → el
 *  back auto-crea un caso si el cliente no tiene ninguno elegible. telefono y ≥1 acreedor
 *  son requeridos (400 si faltan). */
export const createProcess = async (
  { clientId, caseId, telefono, creditors },
  signal
) => {
  const response = await api.post(
    '/admin-negotiation/processes',
    { clientId, caseId, telefono, creditors },
    { signal }
  );
  return response.data;
};

/** Check viability_pending de un caso (inyectado por GC), o null si no hay. */
export const getPendingProcessByCase = async (caseId, signal) => {
  try {
    const response = await api.get(
      `/admin-negotiation/processes/pending-by-case/${caseId}`,
      { signal }
    );
    return response.data ?? null;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
};

/** Check viability_pending cuya tarea trigger (SourceTaskId) es taskId, o null si no
 *  hay. Interceptor de las tabs: por tarea (no por caso) para no interceptar tareas
 *  ajenas al trigger. */
export const getPendingProcessByTask = async (taskId, signal) => {
  try {
    const response = await api.get(
      `/admin-negotiation/processes/pending-by-task/${taskId}`,
      { signal }
    );
    return response.data ?? null;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
};

/** Decide un proceso viability_pending: approve (exige ≥1 acreedor) o reject
 *  (acreedores opcionales; rejectionReason se guarda como historia). */
export const decideProcessViability = async (
  checkId,
  { decision, creditors, rejectionReason },
  signal
) => {
  const response = await api.post(
    `/admin-negotiation/processes/${checkId}/viability-decision`,
    { decision, creditors, rejectionReason },
    { signal }
  );
  return response.data;
};

/** Lista de acreedores de un check con currentArrearsDays (mora actual) calculado. */
export const getProcessCreditors = async (checkId, signal) => {
  const response = await api.get(
    `/admin-negotiation/processes/${checkId}/creditors`,
    { signal }
  );
  return response.data ?? [];
};

// ── Reglas de mensajes + settings del flujo (config API) ────────────────────
// Backend: AdminNegotiationConfigController (api/admin-negotiation).

/** Lista de reglas de mensajes del flujo. */
export const getMessageRules = async (signal) => {
  const response = await api.get('/admin-negotiation/message-rules', {
    signal,
  });
  return response.data ?? [];
};

/** Crea una regla de mensaje { ruleType, daysValue?, messageBody, active }. */
export const createMessageRule = async (rule, signal) => {
  const response = await api.post('/admin-negotiation/message-rules', rule, {
    signal,
  });
  return response.data;
};

/** Edita una regla de mensaje por id. */
export const updateMessageRule = async (id, rule, signal) => {
  const response = await api.put(
    `/admin-negotiation/message-rules/${id}`,
    rule,
    { signal }
  );
  return response.data;
};

/** Elimina una regla de mensaje por id. */
export const deleteMessageRule = async (id, signal) => {
  const response = await api.delete(`/admin-negotiation/message-rules/${id}`, {
    signal,
  });
  return response.data;
};

/** Nomenclaturas de tarea final ({ code, name }) de los configuradores de tarea
 *  habilitados. Alimenta el ComboBox de la tarea final en la configuración; al
 *  elegir un code se autocompleta el name. */
export const getTaskNomenclatures = async (signal) => {
  const response = await api.get('/admin-negotiation/task-nomenclatures', {
    signal,
  });
  return response.data ?? [];
};

/** Settings del flujo (upsert single-row): { debtDaysNeeded, minCreditorsRequired,
 *  documentExpiryDays, finalTaskNomenclature, finalTaskName }. */
export const getFlowSettings = async (signal) => {
  const response = await api.get('/admin-negotiation/flow-settings', {
    signal,
  });
  return response.data ?? null;
};

/** Setea (upsert) los settings del flujo. Devuelve los resultantes. */
export const setFlowSettings = async (settings, signal) => {
  const response = await api.put('/admin-negotiation/flow-settings', settings, {
    signal,
  });
  return response.data ?? null;
};

// ── Documentos ────────────────────────────────────────────────────────────────

/** Sube un documento (multipart) → clasifica y decide por umbral. */
export const uploadDocument = async (
  { contactId, viabilityCheckId, expectedDocs, file },
  signal
) => {
  const form = new FormData();
  form.append('contactId', contactId);
  if (viabilityCheckId != null)
    form.append('viabilityCheckId', String(viabilityCheckId));
  if (expectedDocs) form.append('expectedDocs', expectedDocs);
  form.append('file', file);
  const response = await api.post('/admin-negotiation/documents', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  });
  return response.data;
};

/** Lista de documentos por contacto, filtrable por estado. */
export const getDocuments = async ({ contactId, status }, signal) => {
  const response = await api.get('/admin-negotiation/documents', {
    params: { contactId, status },
    signal,
  });
  return response.data || [];
};

/** URL presignada del documento (inline = ver; si no, descargar). */
export const getDocumentFileUrl = async (
  id,
  { inline = false } = {},
  signal
) => {
  const response = await api.get(
    `/admin-negotiation/documents/${id}/file-url`,
    {
      params: { inline },
      signal,
    }
  );
  return response.data?.url || null;
};

/** Decisión interna del supervisor sobre un needs_review: accept/discard.
 * En accept se puede asignar el doc a un item del checklist (checklistItemId).
 * notifyClient=false = decisión silenciosa (sin WhatsApp al cliente); default true
 * mantiene intacto el comportamiento de los callers existentes (pasan 3 args). */
export const decideDocument = async (
  id,
  decision,
  checklistItemId,
  notifyClient = true,
  signal
) => {
  const response = await api.patch(
    `/admin-negotiation/documents/${id}/decision`,
    { decision, checklistItemId: checklistItemId ?? null, notifyClient },
    { signal }
  );
  return response.data;
};

// ── Requests + checklist (Parte 2) ──────────────────────────────────────────

/** Tabla de requests = checks aprobados con su summary de checklist. Paginada
 *  (server-side) y filtrable por estado derivado (approved/rejected/pending),
 *  búsqueda (RUT/nombre), estado de recolección y rango de fecha de recolección
 *  (collectionFrom/collectionTo, ISO yyyy-MM-dd). Devuelve { items, totalCount }. */
export const getDocumentRequests = async (
  {
    page,
    pageSize,
    status,
    search,
    recolStatus,
    collectionFrom,
    collectionTo,
    assignedUserId,
  } = {},
  signal
) => {
  const response = await api.get('/admin-negotiation/documents/requests', {
    params: {
      page,
      pageSize,
      status: status || undefined,
      search: search || undefined,
      recolStatus: recolStatus || undefined,
      collectionFrom: collectionFrom || undefined,
      collectionTo: collectionTo || undefined,
      assignedUserId: assignedUserId || undefined,
    },
    signal,
  });
  return response.data ?? { items: [], totalCount: 0 };
};

/** Detalle del checklist de un request (check aprobado). */
export const getRequestChecklist = async (viabilityCheckId, signal) => {
  const response = await api.get(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/checklist`,
    { signal }
  );
  return response.data;
};

/** Documentos recibidos del contacto sin clasificar (pool needs_review sin ítem
 *  asignado) de un request, orden Create_At DESC. [] fallback. */
export const getUnclassifiedDocuments = async (viabilityCheckId, signal) => {
  const response = await api.get(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/unclassified-documents`,
    { signal }
  );
  return response.data ?? [];
};

/** Asigna documentos del pool a ítems del checklist (marca cada ítem recibido; el doc
 *  sigue needs_review). assignments = [{ documentId, checklistItemId }]. Devuelve
 *  { checklist, unclassified, failed } ya refrescado post-asignación. */
export const assignDocuments = async (
  viabilityCheckId,
  assignments,
  signal
) => {
  const response = await api.post(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/assign-documents`,
    { assignments },
    { signal }
  );
  return response.data;
};

/** Revisión del lote por el supervisor → reporta a EasyLawyer (legal-review): por ítem
 *  aprobado/rechazado (+motivo). items = [{ checklistItemId, approved, reason }]. */
export const reviewBatch = async (viabilityCheckId, items, signal) => {
  const response = await api.post(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/legal-review`,
    { items },
    { signal }
  );
  return response.data;
};

/** Aprueba la documentación completa del request → el backend manda el WhatsApp de
 *  resolución al cliente. Exige checklist completo (409 si quedan obligatorios pendientes).
 *  Devuelve el checklist actualizado. */
export const approveDocumentation = async (checkId, signal) => {
  const response = await api.post(
    `/admin-negotiation/documents/requests/${checkId}/approve`,
    {},
    { signal }
  );
  return response.data;
};

/** Termina (cancela) un proceso en recolección: estado terminal `canceled` + razón KPI-able +
 *  cascada de los ítems sin disponer (pendiente/recibido/expirado) → `rechazado`. reason = code
 *  C2 (client_abandoned | no_response | service_change | non_viable | other); comment es
 *  obligatorio cuando reason es `other` (validado también en el back). 409 si el proceso no
 *  está en recolección o ya fue aprobado. Devuelve el checklist actualizado (espejo de
 *  approveDocumentation). */
export const cancelProcess = async (checkId, reason, comment, signal) => {
  const response = await api.post(
    `/admin-negotiation/documents/requests/${checkId}/cancel`,
    { reason, comment },
    { signal }
  );
  return response.data;
};

/** Re-solicita YA al cliente los documentos pendientes del request (nudge inmediato, sin
 *  esperar el recordatorio). documentLabels = nombres de los docs puntuales que se re-piden;
 *  solo viaja si la lista trae elementos. reason = motivo que escribe el supervisor
 *  (obligatorio; el back responde 400 si falta). Devuelve { requestSent, checklist }:
 *  requestSent es false cuando no hay regla de mensaje activa o EasyLawyer falló (el front
 *  muestra un aviso de advertencia, nunca un success falso). */
export const requestDocumentsAgain = async (
  viabilityCheckId,
  documentLabels,
  reason,
  signal
) => {
  // reason es obligatorio en el back (400 si falta): se concatena al texto de la regla
  // user_rerequest y viaja al cliente por WhatsApp y por el correo de coexistencia.
  const body =
    Array.isArray(documentLabels) && documentLabels.length > 0
      ? { documentLabels, reason }
      : { reason };
  const response = await api.post(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/re-request`,
    body,
    { signal }
  );
  return response.data;
};

/** Completa/dispone a mano un ítem del checklist: mode 'delivered' → aprobado (el cliente ya lo
 *  entregó antes) | 'not_applicable' → no_aplica (ya no se necesita) | 'rejected' → rechazado
 *  (se rechaza el ítem; bloquea "Aprobar documentación"). No notifica al cliente. 'rejected'
 *  acepta ítems en pendiente/recibido/expirado. Devuelve el checklist refrescado. */
export const manualCompleteItem = async (
  viabilityCheckId,
  checklistItemId,
  mode,
  signal
) => {
  const response = await api.post(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/checklist-items/${checklistItemId}/manual-complete`,
    { mode },
    { signal }
  );
  return response.data;
};

/** Rechaza un documento del checklist con motivo (⋮ "Rechazar"): el ítem vuelve a `pendiente` (NO
 *  cancela/bloquea el proceso). reRequest=true → re-pide el documento al cliente por WhatsApp con el
 *  motivo como cuerpo del mensaje; false → solo registra el motivo. Devuelve el checklist refrescado.
 *  400 si falta el motivo o el ítem no se puede rechazar; 404 si el check/ítem no existen. */
export const rejectDocumentItem = async (
  viabilityCheckId,
  checklistItemId,
  { reason, reRequest = true },
  signal
) => {
  const response = await api.post(
    `/admin-negotiation/documents/requests/${viabilityCheckId}/checklist-items/${checklistItemId}/reject`,
    { reason, reRequest },
    { signal }
  );
  return response.data;
};

/** Historial de mensajes outbound de un request (check aprobado), orden Create_At DESC.
 *  Cada fila: { id, ruleType, status, statusCode, errorDetail, sentAt, createAt }.
 *  Solo lectura (observabilidad de envíos: sent/failed/skipped/stub). [] fallback. */
export const getOutboundMessages = async (checkId, signal) => {
  const response = await api.get(
    `/admin-negotiation/documents/requests/${checkId}/outbound-messages`,
    { signal }
  );
  return response.data ?? [];
};

/** Reenvía manualmente un mensaje outbound en estado 'failed' (reintenta el envío a
 *  EasyLawyer/WhatsApp, ignorando la cola de reintentos). 200 → OutboundMessageItemDto
 *  refrescado; 404 si la fila no existe; 409 si no está 'failed' o el guard por tipo lo
 *  prohíbe (mensaje en err.response.data.message). */
export const resendOutboundMessage = async (id, signal) => {
  const response = await api.post(
    `/admin-negotiation/documents/outbound-messages/${id}/resend`,
    null,
    { signal }
  );
  return response.data;
};

// ── Catálogo de documentos requeridos (CRUD + auditoría) ────────────────────
// Catálogo dinámico en BD, editable desde el submenú "Documentos Requeridos".
// Backend: AdminNegotiationRequiredDocumentController
// (api/admin-negotiation/required-documents). Toda la lista activa se envía a
// EasyLawyer en cada proceso (no hay selección por-caso). Borrado = soft-delete.

/** Lista el catálogo de documentos requeridos. includeInactive=true trae también
 *  los desactivados (para la vista de administración). */
export const getRequiredDocumentsCatalog = async (
  { includeInactive = false } = {},
  signal
) => {
  const response = await api.get('/admin-negotiation/required-documents', {
    params: { includeInactive },
    signal,
  });
  return response.data || [];
};

/** Crea un documento requerido { name, description?, sourceUrl?, categoria, condicion }. */
export const createRequiredDocument = async (body, signal) => {
  const response = await api.post(
    '/admin-negotiation/required-documents',
    body,
    { signal }
  );
  return response.data;
};

/** Actualiza un documento requerido por id (+ isActive? para reactivar). */
export const updateRequiredDocument = async (id, body, signal) => {
  const response = await api.put(
    `/admin-negotiation/required-documents/${id}`,
    body,
    { signal }
  );
  return response.data;
};

/** Soft-delete (IsActive=0) de un documento requerido por id. Idempotente. */
export const deleteRequiredDocument = async (id, signal) => {
  const response = await api.delete(
    `/admin-negotiation/required-documents/${id}`,
    { signal }
  );
  return response.data;
};

// ── Supervisores (Parte 5) ──────────────────────────────────────────────────
// Lista gestionable de usuarios que reciben los avisos por email en los 2 gates
// del flujo (datos listos / documentos listos). Backend:
// AdminNegotiationSupervisorController (api/admin-negotiation/supervisors).

/** Lista de supervisores (nombre/email/estado). */
export const getSupervisors = async (signal) => {
  const response = await api.get('/admin-negotiation/supervisors', { signal });
  return response.data || [];
};

/** Agrega un supervisor por userId (usuario existente). Dedup por UserId en el back. */
export const addSupervisor = async (userId, signal) => {
  const response = await api.post(
    '/admin-negotiation/supervisors',
    { userId },
    { signal }
  );
  return response.data;
};

/** Quita un supervisor por su Id. */
export const removeSupervisor = async (id, signal) => {
  const response = await api.delete(`/admin-negotiation/supervisors/${id}`, {
    signal,
  });
  return response.data;
};
